import { getStorageItem, setStorageItem } from './utils/xummStorage';

export type AssetClass = 'stable' | 'fiat' | 'rwa' | 'utility' | 'other';

export type XrplIssuedAsset = {
  id: string;
  symbol: string; // e.g. USDT
  issuer: string; // r... issuer address
  name: string; // display name
  assetClass: AssetClass;
  description?: string;
  icon?: string; // optional emoji/short label
};

const STORAGE_KEY = 'uny_asset_registry_v1';

export const DEFAULT_ASSETS: XrplIssuedAsset[] = [
  {
    id: 'usdt-rpp12',
    symbol: 'USDT',
    issuer: 'rpP12ND2K7ZRzXZBEUnQM2i18tMGytXnW1',
    name: 'XRPL USDT (IOU)',
    assetClass: 'stable',
    description: 'Issued-currency IOU on XRPL. Recipient must set a trust line.'
  },
  {
    id: 'usd-rpp12',
    symbol: 'USD',
    issuer: 'rpP12ND2K7ZRzXZBEUnQM2i18tMGytXnW1',
    name: 'XRPL USD (IOU)',
    assetClass: 'fiat',
    description: 'Issued-currency IOU on XRPL.'
  },
  {
    id: 'rlusd-example',
    symbol: 'RLUSD',
    issuer: 'rrrrrrrrrrrrrrrrrrrrBZbvji',
    name: 'RLUSD (example issuer)',
    assetClass: 'stable',
    description: 'Example placeholder issuer. Replace with the real issuer you intend to use.'
  }
];

function safeParse(json: string | null): unknown {
  if (!json) return null;
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export async function loadAssetRegistry(): Promise<XrplIssuedAsset[]> {
  const stored = await getStorageItem(STORAGE_KEY);
  const parsed = safeParse(stored);
  if (!Array.isArray(parsed)) return [...DEFAULT_ASSETS];

  const cleaned: XrplIssuedAsset[] = parsed
    .filter((x) => typeof x === 'object' && x !== null)
    .map((x) => x as Partial<XrplIssuedAsset>)
    .filter((x) => typeof x.id === 'string' && typeof x.symbol === 'string' && typeof x.issuer === 'string' && typeof x.name === 'string')
    .map((x) => ({
      id: x.id as string,
      symbol: (x.symbol as string).toUpperCase(),
      issuer: x.issuer as string,
      name: x.name as string,
      assetClass: (x.assetClass as AssetClass) || 'other',
      description: x.description,
      icon: x.icon
    }));

  return cleaned.length ? cleaned : [...DEFAULT_ASSETS];
}

export async function saveAssetRegistry(assets: XrplIssuedAsset[]): Promise<void> {
  await setStorageItem(STORAGE_KEY, JSON.stringify(assets));
}

export function upsertAsset(assets: XrplIssuedAsset[], asset: XrplIssuedAsset): XrplIssuedAsset[] {
  const idx = assets.findIndex((a) => a.id === asset.id);
  if (idx === -1) return [...assets, asset];
  const copy = [...assets];
  copy[idx] = asset;
  return copy;
}

export function removeAsset(assets: XrplIssuedAsset[], id: string): XrplIssuedAsset[] {
  return assets.filter((a) => a.id !== id);
}
